import { CartAddPendingEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { prefersReducedMotion, fetchCartJsonWithMeta } from '@theme/utilities';

let pendingCount = 0;
/** @type {string[]} */
const pendingOptimisticIds = [];

/**
 * @param {string} [preferredId]
 * @returns {string}
 */
function consumeOptimisticId(preferredId = '') {
  if (preferredId) {
    const index = pendingOptimisticIds.indexOf(preferredId);
    if (index !== -1) {
      pendingOptimisticIds.splice(index, 1);
      return preferredId;
    }
  }
  return pendingOptimisticIds.shift() || '';
}

// ─── Checkout button loading state ───────────────────────────────────────────

/**
 * @param {boolean} adding
 */
function setAddingState(adding) {
  const drawer = document.querySelector('cart-drawer-component');
  drawer?.toggleAttribute('cart-adding', adding);

  const btn = /** @type {HTMLButtonElement | null} */ (
    document.getElementById('checkout')
    || document.querySelector('.cart__checkout-button')
  );
  if (btn) {
    btn.classList.toggle('is-loading', adding);
    btn.setAttribute('aria-busy', adding ? 'true' : 'false');
    btn.disabled = adding;
  }
}

/**
 * Slide-down fade-in enter animation — inverse of `.removing` / `removeRow`.
 * @param {HTMLElement} row
 */
function animateOptimisticRowAdd(row) {
  if (!(row instanceof HTMLElement) || prefersReducedMotion()) return;

  const startAnimation = () => {
    const measuredHeight = row.clientHeight;
    if (measuredHeight <= 0) return;

    const cleanup = () => {
      row.classList.remove('adding');
      row.style.removeProperty('--row-height');
      row.style.removeProperty('overflow');
      row.style.removeProperty('height');
      row.style.removeProperty('opacity');
      row.style.removeProperty('margin-bottom');
      row.style.removeProperty('padding-bottom');
    };

    row.style.setProperty('--row-height', `${measuredHeight}px`);
    row.style.overflow = 'hidden';
    row.style.height = '0';
    row.style.opacity = '0';
    row.style.marginBottom = '0';
    row.style.paddingBottom = '0';
    void row.offsetHeight;
    row.style.height = '';
    row.style.opacity = '';

    row.addEventListener('animationend', (event) => {
      if (event.target !== row || event.animationName !== 'addRow') return;
      cleanup();
    }, { once: true });
    row.classList.add('adding');
  };

  // Wait for layout after DOM insert — clientHeight is 0 if measured synchronously.
  requestAnimationFrame(() => {
    requestAnimationFrame(startAnimation);
  });
}

/**
 * @param {string} optimisticId
 * @returns {HTMLElement | null}
 */
function findOptimisticRow(optimisticId) {
  if (optimisticId) {
    const match = document.querySelector(
      `.cart-items__table-row[data-optimistic-id="${CSS.escape(optimisticId)}"]`
    );
    if (match instanceof HTMLElement) return match;
  }

  const fallback = document.querySelector('.cart-items__table-row[data-optimistic="true"]');
  return fallback instanceof HTMLElement ? fallback : null;
}

// ─── Optimistic cart item shell ───────────────────────────────────────────────

/** Decode HTML entities so esc() does not double-encode pre-encoded translation strings. */
/**
 * @param {string} str
 * @returns {string}
 */
function decodeHtmlEntities(str) {
  if (!str || !str.includes('&')) return String(str);
  const el = document.createElement('textarea');
  el.innerHTML = String(str);
  return el.value;
}

/** Escape a string for safe insertion into HTML attribute values or text nodes. */
/**
 * @param {string} str
 * @returns {string}
 */
function esc(str) {
  return decodeHtmlEntities(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** @returns {Record<string, string>} */
function getThemeTranslations() {
  return /** @type {Record<string, string>} */ (window.Theme?.translations || {});
}

/** @returns {{ totalPriceCents: number, freeShippingThresholdCents: number, moneyFormat: string }} */
function getThemeCartConfig() {
  const cart = /** @type {{ totalPriceCents?: number, freeShippingThresholdCents?: number, moneyFormat?: string }} */ (
    window.Theme?.cart || {}
  );
  return {
    totalPriceCents: Number(cart.totalPriceCents) || 0,
    freeShippingThresholdCents: Number(cart.freeShippingThresholdCents) || 0,
    moneyFormat: cart.moneyFormat || '{{amount}}',
  };
}

/**
 * @param {number} moneyValueCents
 * @returns {string}
 */
function formatMoneyFromCents(moneyValueCents) {
  const template = getThemeCartConfig().moneyFormat || '{{amount}}';
  const placeholderMatch = template.match(/\{\{\s*(\w+)\s*\}\}/);
  const placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';

  let precision = 2;
  let thousandsSeparator = ',';
  let decimalSeparator = '.';

  switch (placeholder) {
    case 'amount_no_decimals':
      precision = 0;
      break;
    case 'amount_with_comma_separator':
      thousandsSeparator = '.';
      decimalSeparator = ',';
      break;
    case 'amount_no_decimals_with_comma_separator':
      precision = 0;
      thousandsSeparator = '.';
      decimalSeparator = ',';
      break;
    case 'amount_with_space_separator':
      thousandsSeparator = ' ';
      break;
    case 'amount_no_decimals_with_space_separator':
      precision = 0;
      thousandsSeparator = ' ';
      break;
    case 'amount_with_apostrophe_separator':
      thousandsSeparator = "'";
      break;
    default:
      break;
  }

  const roundedNumber = (moneyValueCents / 100).toFixed(precision);
  const [intPart, decPart] = roundedNumber.split('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
  const formattedValue = decPart ? `${formattedInt}${decimalSeparator}${decPart}` : formattedInt;

  return template.replace(placeholderMatch ? placeholderMatch[0] : '{{amount}}', formattedValue);
}

/**
 * @param {number} projectedTotalCents
 * @returns {{ copyHtml: string, percent: number, unlocked: boolean }}
 */
function getShippingProgressState(projectedTotalCents) {
  const translations = getThemeTranslations();
  const { freeShippingThresholdCents: threshold } = getThemeCartConfig();
  const goalMarkup = translations.shipping_progress_goal_markup || translations.shipping_progress_goal || '';

  if (threshold <= 0) {
    return { copyHtml: '', percent: 0, unlocked: false };
  }

  const remainingCents = Math.max(0, threshold - projectedTotalCents);
  const percent = Math.min(100, Math.round((projectedTotalCents / threshold) * 100));
  const unlocked = remainingCents === 0;

  if (unlocked) {
    let successText = translations.shipping_progress_success || '';
    if (successText.includes('[goal]')) {
      successText = successText.replace('[goal]', goalMarkup);
    } else if (goalMarkup) {
      successText = `${successText} ${goalMarkup}`.trim();
    }
    return { copyHtml: successText, percent, unlocked: true };
  }

  const remainingMoney = formatMoneyFromCents(remainingCents);
  let pendingText = translations.shipping_progress_pending || '';
  if (pendingText.includes('[amount]')) {
    pendingText = pendingText.replace('[amount]', remainingMoney);
  }
  if (pendingText.includes('[goal]')) {
    pendingText = pendingText.replace('[goal]', goalMarkup);
  } else if (goalMarkup) {
    pendingText = `${pendingText} ${goalMarkup}`.trim();
  }

  return { copyHtml: pendingText, percent, unlocked: false };
}

/**
 * @param {number} addedLineCents
 */
function updateOptimisticShippingProgress(addedLineCents) {
  const { totalPriceCents, freeShippingThresholdCents: threshold } = getThemeCartConfig();
  if (threshold <= 0) return;

  const projectedTotal = totalPriceCents + (Number(addedLineCents) || 0);
  const { copyHtml, percent, unlocked } = getShippingProgressState(projectedTotal);
  const translations = getThemeTranslations();
  const truckIcon = /** @type {{ truck?: string }} */ (window.Theme?.icons || {}).truck || '';
  const ariaLabel = esc(translations.shipping_progress_aria_label || translations.shipping_progress_goal || '');

  const progressBlocks = document.querySelectorAll('.cart-drawer__shipping-progress');
  progressBlocks.forEach((block) => {
    if (!(block instanceof HTMLElement)) return;

    const copyEl = block.querySelector('.cart-drawer__shipping-progress-copy');
    if (copyEl instanceof HTMLElement) {
      copyEl.innerHTML = copyHtml;
    }

    let iconEl = block.querySelector('.cart-drawer__shipping-progress-icon');
    if (unlocked && truckIcon) {
      if (!(iconEl instanceof HTMLElement)) {
        block.querySelector('.cart-drawer__shipping-progress-text')?.insertAdjacentHTML(
          'beforeend',
          `<span class="cart-drawer__shipping-progress-icon svg-wrapper" aria-hidden="true">${truckIcon}</span>`
        );
        iconEl = block.querySelector('.cart-drawer__shipping-progress-icon');
      }
      if (iconEl instanceof HTMLElement) {
        iconEl.hidden = false;
      }
    } else if (iconEl instanceof HTMLElement) {
      iconEl.hidden = true;
    }

    const track = block.querySelector('.cart-drawer__shipping-progress-track');
    if (track instanceof HTMLElement) {
      track.setAttribute('aria-valuenow', String(percent));
      track.setAttribute('aria-label', ariaLabel);
    }

    const fill = block.querySelector('.cart-drawer__shipping-progress-fill');
    if (fill instanceof HTMLElement) {
      fill.style.width = `${percent}%`;
    }
  });
}

/**
 * Parse money-like text into a number for compare-price checks.
 * Handles common formats like "83,95", "1,234.56", "1.234,56".
 * @param {string} value
 * @returns {number | null}
 */
function parseMoneyNumber(value) {
  const raw = (value || '').trim().replace(/[^\d.,-]/g, '');
  if (!raw) return null;

  const hasComma = raw.includes(',');
  const hasDot = raw.includes('.');
  let normalized = raw;

  if (hasComma && hasDot) {
    if (raw.lastIndexOf(',') > raw.lastIndexOf('.')) {
      normalized = raw.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = raw.replace(/,/g, '');
    }
  } else if (hasComma) {
    normalized = raw.replace(',', '.');
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Collapse whitespace so money values stay on one line.
 * @param {string} value
 * @returns {string}
 */
function normalizeInlineText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

/**
 * Builds one <tr> shell that mirrors the real cart-items__table-row structure.
 * The row carries `data-optimistic` so it can be found and removed if needed,
 * though in practice morphSection will replace it before anyone notices.
 *
 * @param {{ imageUrl: string, productTitle: string, price: string, productUrl: string, optimisticId?: string, qty?: number, variantSummary?: string, variantId?: string, comparePrice?: string, priceEach?: string, hasNro?: boolean, isNroSelected?: boolean, subscriptionDiscountPercent?: string, subscriptionReceive?: string, nroDiscountLabel?: string }} data
 */
function buildOptimisticRow({
  imageUrl,
  productTitle,
  price,
  productUrl,
  optimisticId = '',
  qty = 1,
  variantSummary = '',
  variantId = '',
  comparePrice = '',
  priceEach = '',
  hasNro = false,
  isNroSelected = false,
  subscriptionDiscountPercent = '',
  subscriptionReceive = '',
  nroDiscountLabel = '',
}) {
  const translations = getThemeTranslations();
  const discountedLabel = esc(translations.cart_pending_discounted || '');
  const neverRunOutLabel = esc(translations.cart_pending_never_run_out || '');
  const neverRunOutSubtitle = esc(translations.cart_pending_never_run_out_subtitle || '');
  const decreaseQuantityLabel = esc(translations.cart_pending_decrease_quantity || '');
  const increaseQuantityLabel = esc(translations.cart_pending_increase_quantity || '');
  const quantityLabel = esc(translations.cart_pending_quantity || '');
  const removeLabel = esc(translations.cart_pending_remove || '');
  const href = esc(productUrl || '#');
  const title = esc((productTitle || '').trim() || ' ');
  const summary = esc((variantSummary || '').trim());
  const displayPrice = normalizeInlineText(price || '') || ' ';
  const rawComparePrice = normalizeInlineText(comparePrice || '');
  const priceAmount = parseMoneyNumber(displayPrice);
  const compareAmount = parseMoneyNumber(rawComparePrice);
  const shouldShowCompare = isNroSelected
    ? /\d/.test(rawComparePrice)
    : !!rawComparePrice && compareAmount != null && (priceAmount == null || compareAmount > priceAmount);
  const displayComparePrice = shouldShowCompare ? esc(rawComparePrice) : '';
  const displayPriceEach = esc(normalizeInlineText(priceEach || ''));
  const displaySubscriptionDiscountPercent = esc((subscriptionDiscountPercent || '').trim());
  const displaySubscriptionReceive = esc((subscriptionReceive || '').trim());
  const displayNroDiscountLabel = esc(normalizeInlineText(nroDiscountLabel || ''));
  const displayQty = Number.isFinite(qty) && qty > 0 ? Math.floor(qty) : 1;
  const imgHtml = imageUrl
    ? `<a href="${href}" class="cart-items__media-container" style="--ratio:1;" tabindex="-1">
         <img src="${esc(imageUrl)}" class="cart-items__media-image border-style" alt="${title}" loading="eager">
       </a>`
    : `<div class="cart-items__media-container" style="--ratio:1;"></div>`;

  return `
    <tr role="row" class="cart-items__table-row" data-optimistic="true" data-optimistic-id="${esc(optimisticId)}">
      <td class="cart-items__media" role="cell" headers="productImage">
        ${imgHtml}
      </td>
      <td class="cart-items__details cart-primary-typography" role="cell" headers="productInformation">
        <p><a href="${href}" class="cart-items__title">${title}</a></p>
        ${summary
      ? `<dl class="cart-items__variants">
          <div class="cart-items__variant"><dd>${summary}</dd></div>
        </dl>`
      : ''}
        ${isNroSelected && displaySubscriptionReceive
      ? `<div class="cart-items__subscription">
          <span class="cart-items__subscription-receive-wrap">
            <span class="cart-items__subscription-label cart-items__subscription-icon">
              <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g id="icon-arrows-clock-wise" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M13.766 7.79h3.75V4.04"></path>
                  <path d="M5.14 5.14a6.867 6.867 0 0 1 9.72 0l2.656 2.65M6.234 12.21h-3.75v3.75"></path>
                  <path d="M14.86 14.86a6.869 6.869 0 0 1-9.72 0l-2.656-2.65"></path>
                </g>
              </svg>
            </span>
            <span class="cart-items__subscription-receive">${displaySubscriptionReceive}</span>
          </span>
        </div>`
      : ''}
      </td>
      <td class="cart-items__price" role="cell" headers="productTotal">
        <div class="cart-items__price-content">
          <div class="cart-items__price-row${isNroSelected ? ' cart-items__price-row--subscription' : ''}">
            ${displayComparePrice ? `<span class="cart-items__price-compare compare-at-price">${displayComparePrice}</span>` : ''}
            <text-component class="cart-items__price-total${displayComparePrice ? ' cart-items__price-total--discounted' : ''}" value="${esc(displayPrice)}">${esc(displayPrice)}</text-component>
          </div>
          ${displayPriceEach ? `<span class="cart-items__price-each cart-secondary-typography">${displayPriceEach}</span>` : ''}
        </div>
      </td>
      <td class="cart-items__quantity" role="cell" headers="quantity">
        <div class="cart-items__quantity-controls${isNroSelected ? ' cart-items__quantity-controls--subscription' : ''}" aria-disabled="true">
          ${isNroSelected
      ? `${displaySubscriptionDiscountPercent
        ? `<span class="cart-items__subscription-discount">
                <span class="cart-items__subscription-discount-percent">${displaySubscriptionDiscountPercent}</span>
                <span class="cart-items__subscription-discount-text">${discountedLabel}</span>
                <span class="cart-items__subscription-discount-icon"><svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path id="icon-check" d="m10.125 3.375-5.25 5.25L2.25 6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg></span>
              </span>`
        : ''}`
      : `<div class="quantity-selector-wrapper">
            <cart-quantity-selector-component class="quantity-selector cart-primary-typography" data-variant-id="${esc(variantId)}">
              <button class="button quantity-minus button-unstyled" type="button" name="minus" ref="minusButton" disabled>
                <span class="visually-hidden">${decreaseQuantityLabel}</span><span class="svg-wrapper icon-plus">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
                  </svg>
                </span>
              </button>
              <input type="number" value="${displayQty}" min="1" step="1" readonly aria-label="${quantityLabel}" ref="quantityInput" data-cart-quantity="${displayQty}" data-min="1">
              <button class="button quantity-plus button-unstyled" type="button" name="plus" ref="plusButton" disabled>
                <span class="visually-hidden">${increaseQuantityLabel}</span><span class="svg-wrapper icon-plus">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path class="vertical" d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
                    <path class="horizontal" d="M7 2.75L7 11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
                  </svg>
                </span>
              </button>
            </cart-quantity-selector-component>
          </div>`}
          <button class="button button--tertiary cart-items__remove" type="button" disabled>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path class="remove-icon-bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
              <path class="remove-icon-top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
            <span class="visually-hidden">${removeLabel}</span>
          </button>
        </div>
      </td>
      <td class="cart-items__error hidden" role="cell" headers="quantity">
        <div class="cart-item__error" role="alert">
          <span class="svg-wrapper">
            <svg class="icon icon-error" viewBox="0 0 13 13">
              <circle cx="6.5" cy="6.5" r="5.5" stroke="#fff" stroke-width="var(--icon-stroke-width)"></circle>
              <circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width=".7"></circle>
              <path fill="#fff" d="m5.874 3.528.1 4.044h1.053l.1-4.044zm.627 6.133c.38 0 .68-.288.68-.656s-.3-.656-.68-.656-.681.288-.681.656.3.656.68.656"></path>
            </svg>
          </span>
          <small class="cart-item__error-text cart-primary-typography"></small>
        </div>
      </td>
      ${hasNro && !isNroSelected
      ? `<td class="cart-items__nro-cell" role="cell" headers="productInformation">
        <div class="cart-items__nro" data-optimistic-nro="true">
          <button type="button" class="cart-items__nro-toggle button-unstyled" aria-expanded="false" disabled>
            <span class="cart-items__nro-toggle-main">
              <span class="cart-items__nro-toggle-checkbox" aria-hidden="true"></span>
              <span class="cart-items__nro-toggle-copy">
                <span class="cart-items__nro-toggle-text">${neverRunOutLabel}</span>
                <span class="cart-items__nro-toggle-subtitle">${neverRunOutSubtitle}</span>
              </span>
            </span>
            <span class="cart-items__nro-toggle-discount${displayNroDiscountLabel ? '' : ' cart-optimistic-skeleton'}">${displayNroDiscountLabel || '\u00a0'}</span>
          </button>
        </div>
      </td>`
      : ''}
    </tr>`;
}

/**
 * @param {HTMLElement} summaryEl
 */
function ensureTaxNoteInSummary(summaryEl) {
  const container = summaryEl.querySelector('.cart__total-container');
  if (!(container instanceof HTMLElement) || container.querySelector('.tax-note')) return;

  const taxNote = esc(getThemeTranslations().taxes_included_shipping_at_checkout || '');
  if (!taxNote) return;

  container.insertAdjacentHTML(
    'beforeend',
    `<div class="cart__summary-item tax-note cart-primary-typography"><small>${taxNote}</small></div>`
  );
}

function ensureDrawerHasSummary(projectedTotalCents) {
  const drawerDialog = document.querySelector('cart-drawer-component .cart-drawer__dialog');
  if (drawerDialog instanceof HTMLElement) {
    drawerDialog.classList.remove('cart-drawer--empty');
  }

  const emptyHeading = document.querySelector('.cart-drawer__heading--empty');
  if (emptyHeading instanceof HTMLElement) {
    emptyHeading.remove();
  }

  const content = document.querySelector('cart-drawer-component .cart-drawer__content');
  if (!(content instanceof HTMLElement)) return;

  const hasProjectedTotal = projectedTotalCents != null && Number.isFinite(projectedTotalCents);
  const formattedTotal = hasProjectedTotal ? formatMoneyFromCents(projectedTotalCents) : null;

  const existingSummary = content.querySelector('.cart-drawer__summary');
  if (existingSummary instanceof HTMLElement) {
    const totalEl = existingSummary.querySelector('.cart__total-value');
    if (totalEl instanceof HTMLElement) {
      if (formattedTotal) {
        totalEl.textContent = formattedTotal;
        totalEl.setAttribute('value', formattedTotal);
        totalEl.removeAttribute('shimmer');
        totalEl.classList.remove('cart-optimistic-skeleton');
      } else {
        totalEl.textContent = '…';
        totalEl.setAttribute('value', '…');
        totalEl.setAttribute('shimmer', '');
        totalEl.classList.add('cart-optimistic-skeleton');
      }
    }
    ensureTaxNoteInSummary(existingSummary);
    return;
  }

  const translations = getThemeTranslations();
  const estimatedTotal = esc(translations.cart_estimated_total || '');
  const taxNote = esc(translations.taxes_included_shipping_at_checkout || '');
  const checkoutLabel = esc(translations.checkout || '');
  const totalMarkup = formattedTotal
    ? `<text-component class="cart__total-value cart-secondary-typography" value="${esc(formattedTotal)}">${esc(formattedTotal)}</text-component>`
    : `<text-component class="cart__total-value cart-secondary-typography cart-optimistic-skeleton" value="…" shimmer>…</text-component>`;

  content.insertAdjacentHTML(
    'beforeend',
    `<div class="cart-drawer__summary" data-optimistic-summary="true">
      <div class="cart__summary-totals">
        <div class="cart__total-container">
          <span class="cart__summary-item cart__total" role="status">
            <span class="cart__total-label cart-primary-typography">${estimatedTotal}</span>
            ${totalMarkup}
          </span>
          <div class="cart__summary-item tax-note cart-primary-typography">
            <small>${taxNote}</small>
          </div>
        </div>
      </div>
      <div class="cart__ctas">
        <button
          type="submit"
          id="checkout"
          class="cart__checkout-button button"
          name="checkout"
          form="cart-form"
        >
          <span class="cart__checkout-text">${checkoutLabel}</span>
          <span class="cart__checkout-spinner" aria-hidden="true"></span>
        </button>
      </div>
    </div>`
  );
}

/**
 * @param {number} [addedLineCents=0]
 */
function ensureDrawerHeaderPlaceholder(addedLineCents = 0) {
  const header = document.querySelector('cart-drawer-component .cart-drawer__header');
  if (!(header instanceof HTMLElement)) return;

  const { totalPriceCents, freeShippingThresholdCents: threshold } = getThemeCartConfig();
  if (threshold <= 0) return;

  const projectedTotal = totalPriceCents + (Number(addedLineCents) || 0);
  const { copyHtml, percent, unlocked } = getShippingProgressState(projectedTotal);
  const translations = getThemeTranslations();
  const truckIcon = /** @type {{ truck?: string }} */ (window.Theme?.icons || {}).truck || '';
  const shippingAriaLabel = esc(translations.shipping_progress_aria_label || translations.shipping_progress_goal || '');

  if (!header.querySelector('.cart-drawer__heading')) {
    const cartTitle = esc(translations.cart_title || '');

    header.insertAdjacentHTML(
      'afterbegin',
      `<div class="cart-drawer__header-main">
        <h2 class="cart-drawer__heading" id="cart-drawer-heading">${cartTitle}</h2>
        <div class="cart-drawer__shipping-progress">
          <p class="cart-drawer__shipping-progress-text">
            <span class="cart-drawer__shipping-progress-copy">${copyHtml}</span>
            ${unlocked && truckIcon
      ? `<span class="cart-drawer__shipping-progress-icon svg-wrapper" aria-hidden="true">${truckIcon}</span>`
      : `<span class="cart-drawer__shipping-progress-icon svg-wrapper" aria-hidden="true" hidden>${truckIcon}</span>`}
          </p>
          <div
            class="cart-drawer__shipping-progress-track"
            role="progressbar"
            aria-label="${shippingAriaLabel}"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow="${percent}"
          >
            <span class="cart-drawer__shipping-progress-fill" style="width: ${percent}%;"></span>
          </div>
        </div>
      </div>`
    );
  } else {
    updateOptimisticShippingProgress(addedLineCents);
  }
}

function clearEmptyDrawerUI(addedLineCents = 0) {
  const emptyHeading = document.querySelector('.cart-drawer__heading--empty');
  if (emptyHeading instanceof HTMLElement) {
    emptyHeading.remove();
  }

  const emptyButton = document.querySelector('.cart-items__empty-button');
  if (emptyButton instanceof HTMLElement) {
    emptyButton.remove();
  }

  const drawerDialog = document.querySelector('cart-drawer-component .cart-drawer__dialog');
  if (drawerDialog instanceof HTMLElement) {
    drawerDialog.classList.remove('cart-drawer--empty');
  }

  ensureDrawerHeaderPlaceholder(addedLineCents);
}

/**
 * @param {string} optimisticId
 * @returns {HTMLElement | null}
 */
function findOptimisticRowForRollback(optimisticId) {
  if (optimisticId) {
    const match = Array.from(document.querySelectorAll('.cart-items__table-row[data-optimistic-id]')).find(
      (el) => el instanceof HTMLElement && el.getAttribute('data-optimistic-id') === optimisticId
    );
    if (match instanceof HTMLElement) return match;
  }

  const fallback = document.querySelector('.cart-items__table-row[data-optimistic="true"]');
  return fallback instanceof HTMLElement ? fallback : null;
}

/**
 * @param {HTMLElement} row
 */
function clearOptimisticMarkers(row) {
  row.removeAttribute('data-optimistic');
  row.removeAttribute('data-optimistic-id');
  row.removeAttribute('data-pre-merge-qty');
}

/**
 * @param {HTMLElement} row
 * @returns {boolean}
 */
function restoreMergedOptimisticRow(row) {
  const preMergeQty = row.getAttribute('data-pre-merge-qty');
  if (preMergeQty == null) return false;

  const input = row.querySelector('input[ref="quantityInput"], input[data-cart-quantity]');
  if (input instanceof HTMLInputElement) {
    input.value = preMergeQty;
    input.setAttribute('data-cart-quantity', preMergeQty);
  }

  clearOptimisticMarkers(row);
  return true;
}

/**
 * @param {string} optimisticId
 */
function rollbackOptimisticItem(optimisticId) {
  const row = findOptimisticRowForRollback(optimisticId);
  if (!(row instanceof HTMLElement)) return;

  if (restoreMergedOptimisticRow(row)) return;

  row.remove();
}

/**
 * @param {string} variantId
 * @returns {Promise<{ qty: number, rateLimited: boolean }>}
 */
async function getCartVariantQtyMeta(variantId) {
  if (!variantId) return { qty: 0, rateLimited: false };

  const { cart, rateLimited } = await fetchCartJsonWithMeta();
  if (rateLimited || !cart) return { qty: 0, rateLimited };

  const item = cart.items?.find((line) => String(line.variant_id) === String(variantId));
  return { qty: item?.quantity ?? 0, rateLimited: false };
}

/**
 * @param {string} optimisticId
 * @param {string} variantId
 * @returns {Promise<boolean>}
 */
async function shouldSkipRollbackForPartialAdd(optimisticId, variantId) {
  const row = findOptimisticRowForRollback(optimisticId);
  const { qty: cartQty, rateLimited } = await getCartVariantQtyMeta(variantId);

  if (rateLimited) return false;

  if (row instanceof HTMLElement && row.hasAttribute('data-pre-merge-qty')) {
    const preMergeQty = parseInt(row.getAttribute('data-pre-merge-qty') || '0', 10) || 0;
    return cartQty > preMergeQty;
  }

  return cartQty > 0;
}

async function refreshCartItemsSection() {
  const sectionId = document.querySelector('cart-items-component')?.dataset?.sectionId;
  if (!sectionId) return;
  await sectionRenderer.renderSection(sectionId, { cache: false });
}

/**
 * Find an existing cart row for a variant ID.
 * @param {string} variantId
 * @returns {HTMLElement | null}
 */
function findCartRowForVariant(variantId) {
  if (!variantId) return null;

  const qtySelector = document.querySelector(
    `.cart-items__table-row [data-variant-id="${CSS.escape(String(variantId))}"]`
  );
  return qtySelector instanceof HTMLElement ? qtySelector.closest('.cart-items__table-row') : null;
}

/**
 * Bump quantity on an existing row instead of appending a duplicate optimistic row.
 * @param {HTMLElement} row
 * @param {number} qtyToAdd
 * @param {string} optimisticId
 */
function mergeOptimisticIntoExistingRow(row, qtyToAdd, optimisticId) {
  const input = row.querySelector('input[ref="quantityInput"], input[data-cart-quantity]');
  if (input instanceof HTMLInputElement) {
    const current =
      parseInt(input.value, 10) ||
      parseInt(input.getAttribute('data-cart-quantity') || '0', 10) ||
      0;
    row.setAttribute('data-pre-merge-qty', String(current));
    const newQty = current + qtyToAdd;
    input.value = String(newQty);
    input.setAttribute('data-cart-quantity', String(newQty));
  }

  row.setAttribute('data-optimistic', 'true');
  if (optimisticId) {
    row.setAttribute('data-optimistic-id', optimisticId);
  }

  const tbody = row.closest('tbody');
  if (tbody instanceof HTMLElement) {
    tbody.prepend(row);
  }
}

/**
 * Inject a shell cart row so the drawer shows something real immediately,
 * before the Shopify cart API responds. morphSection will replace all of
 * this with accurate server-rendered content once the fetch completes.
 *
 * @param {{ optimisticId?: string, imageUrl?: string, productTitle?: string, price?: string, qty?: number, productUrl?: string, variantSummary?: string, variantId?: string, comparePrice?: string, priceEach?: string, hasNro?: boolean, isNroSelected?: boolean, subscriptionDiscountPercent?: string, subscriptionReceive?: string, linePriceCents?: number, nroDiscountLabel?: string }} detail
 */
function injectOptimisticItem(detail) {
  const wrapper = document.querySelector('.cart-items__wrapper');
  if (!wrapper) return;

  const variantId = detail.variantId || '';
  const qtyToAdd = detail.qty || 1;
  const optimisticId = detail.optimisticId || '';
  const linePriceCents = detail.linePriceCents != null ? Number(detail.linePriceCents) : null;
  const { totalPriceCents } = getThemeCartConfig();
  const projectedTotalCents = linePriceCents != null ? totalPriceCents + linePriceCents : null;

  const existingRow = findCartRowForVariant(variantId);
  const isSubscriptionRow = existingRow?.querySelector(
    '.cart-items__subscription, .cart-items__quantity-controls--subscription'
  );
  if (existingRow instanceof HTMLElement && !detail.isNroSelected && !isSubscriptionRow) {
    mergeOptimisticIntoExistingRow(existingRow, qtyToAdd, optimisticId);
    ensureDrawerHasSummary(projectedTotalCents);
    updateOptimisticShippingProgress(linePriceCents || 0);
    return;
  }

  const row = buildOptimisticRow({
    optimisticId: detail.optimisticId || '',
    imageUrl: detail.imageUrl || '',
    productTitle: detail.productTitle || '',
    price: detail.price || '',
    productUrl: detail.productUrl || '',
    qty: detail.qty || 1,
    variantSummary: detail.variantSummary || '',
    variantId: detail.variantId || '',
    comparePrice: detail.comparePrice || '',
    priceEach: detail.priceEach || '',
    hasNro: detail.hasNro === true,
    isNroSelected: detail.isNroSelected === true,
    subscriptionDiscountPercent: detail.subscriptionDiscountPercent || '',
    subscriptionReceive: detail.subscriptionReceive || '',
    nroDiscountLabel: detail.nroDiscountLabel || '',
  });

  const existingTbody = wrapper.querySelector('.cart-items__table tbody');

  if (existingTbody) {
    // Cart already has items — prepend optimistic row so it appears at the top.
    existingTbody.insertAdjacentHTML('afterbegin', row);
    const insertedRow = findOptimisticRow(optimisticId);
    if (insertedRow) animateOptimisticRowAdd(insertedRow);
  } else {
    // Cart is empty — replace the empty state with a minimal table so the
    // drawer shows the item instead of the "Close Cart" button.
    wrapper.innerHTML = `
      <form action="/cart" class="cart-form" id="cart-form" method="post">
        <div class="cart-items spacing-style">
          <table class="cart-items__table" role="table">
            <thead class="visually-hidden" role="rowgroup">
              <tr role="row" class="cart-items__table-row">
                <th id="productImage" scope="col" role="columnheader"></th>
                <th id="productInformation" scope="col" role="columnheader"></th>
                <th id="quantity" scope="col" role="columnheader"></th>
                <th id="productTotal" scope="col" role="columnheader"></th>
              </tr>
            </thead>
            <tbody role="rowgroup">${row}</tbody>
          </table>
        </div>
      </form>`;
    const insertedRow = findOptimisticRow(optimisticId);
    if (insertedRow) animateOptimisticRowAdd(insertedRow);
  }

  ensureDrawerHasSummary(projectedTotalCents);
  updateOptimisticShippingProgress(linePriceCents || 0);
}

// ─── Event listeners ──────────────────────────────────────────────────────────

document.addEventListener(
  CartAddPendingEvent.eventName,
  (event) => {
    const e = /** @type {CartAddPendingEvent} */ (event);
    const detail = e.detail || {};
    const addedLineCents = detail.linePriceCents != null ? Number(detail.linePriceCents) : 0;
    pendingCount++;
    pendingOptimisticIds.push(detail.optimisticId || '');
    clearEmptyDrawerUI(addedLineCents);
    injectOptimisticItem(detail);
    setAddingState(true);

    const drawer = document.querySelector('cart-drawer-component');
    if (drawer?.hasAttribute('auto-open') && typeof drawer.showDialog === 'function') {
      drawer.showDialog({ immediate: true });
    }
  },
  { capture: true }
);

// CartAddEvent (success or error) fires with source 'product-form-component' — resolve each pending add.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const detail = /** @type {CustomEvent} */ (event).detail?.data;
  if (detail?.source !== 'product-form-component') return;

  const optimisticId = consumeOptimisticId(detail.optimisticId || '');

  const resolvePending = async () => {
    if (detail.didError) {
      const variantId = String(detail.variantId || '');
      const skipRollback = await shouldSkipRollbackForPartialAdd(optimisticId, variantId);
      if (skipRollback) {
        await refreshCartItemsSection();
      } else {
        rollbackOptimisticItem(optimisticId);
      }
    }

    pendingCount = Math.max(0, pendingCount - 1);
    if (pendingCount === 0) setAddingState(false);
  };

  void resolvePending();
});
